/**
 * Cross-platform storage abstraction
 * 
 * Uses native storage on Android/iOS (via Capacitor Preferences)
 * Falls back to localStorage on web
 * 
 * Note: All operations are async to support native platforms
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

/**
 * Set a value in storage
 */
export async function setItem(key: string, value: string): Promise<void> {
  if (isNative) {
    try {
      await Preferences.set({ key, value });
      return;
    } catch {
      // Fallback when native plugin is unavailable/misconfigured.
    }
  }
  localStorage.setItem(key, value);
}

/**
 * Get a value from storage
 */
export async function getItem(key: string): Promise<string | null> {
  if (isNative) {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch {
      // Fallback when native plugin is unavailable/misconfigured.
    }
  }
  return localStorage.getItem(key);
}

/**
 * Remove a value from storage
 */
export async function removeItem(key: string): Promise<void> {
  if (isNative) {
    try {
      await Preferences.remove({ key });
      return;
    } catch {
      // Fallback when native plugin is unavailable/misconfigured.
    }
  }
  localStorage.removeItem(key);
}

/**
 * Clear all storage
 */
export async function clear(): Promise<void> {
  if (isNative) {
    try {
      await Preferences.clear();
      return;
    } catch {
      // Fallback when native plugin is unavailable/misconfigured.
    }
  }
  localStorage.clear();
}

/**
 * Get all keys in storage
 */
export async function keys(): Promise<string[]> {
  if (isNative) {
    try {
      const { keys } = await Preferences.keys();
      return keys;
    } catch {
      // Fallback when native plugin is unavailable/misconfigured.
    }
  }
  return Object.keys(localStorage);
}

/**
 * Helper to set JSON data
 */
export async function setJSON<T>(key: string, data: T): Promise<void> {
  await setItem(key, JSON.stringify(data));
}

/**
 * Helper to get JSON data
 */
export async function getJSON<T>(key: string): Promise<T | null> {
  const value = await getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * Get approximate storage size in bytes
 * Note: Only works on web, returns 0 on native
 */
export async function getStorageSize(): Promise<number> {
  if (isNative) {
    // Native storage doesn't expose size easily
    // Could iterate keys and sum values if needed
    return 0;
  }
  
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const value = localStorage.getItem(key);
      if (value) {
        total += new Blob([key + value]).size;
      }
    }
  }
  return total;
}
